import { isAfter, isValid, startOfDay } from "date-fns";

export enum AvailabilityEnum {
  NOT_AVAILABLE = "surface.300",
  AVAILABLE = "success.500",
  FAST_FILLING = "warning.500",
  SOLD_OUT = "danger.500",
}

export enum AvailabilityColorSchemeEnum {
  NOT_AVAILABLE = "surface",
  AVAILABLE = "success",
  FAST_FILLING = "warning",
  SOLD_OUT = "danger",
}

/** Seats can be selected right now. */
export const isShowBookable = (showStatus?: string): boolean =>
  showStatus === "available" || showStatus === "fast_filling";

/**
 * The seat map can be opened. A show reports `expired` from the moment it
 * *starts*, so a running show is still worth looking at — it opens read-only,
 * because `lock_web` is set by then. A finished show (`show_ended`) is not, and
 * neither is a locked show whose company disallows viewing (`seat_view_allowed`
 * false; absent from older servers, so only an explicit `false` blocks).
 */
export const isShowViewable = (
  showStatus?: string,
  showEnded?: boolean,
  seatViewAllowed?: boolean,
): boolean =>
  seatViewAllowed !== false &&
  (isShowBookable(showStatus) || (showStatus === "expired" && !showEnded));

/**
 * A now-showing movie is on advance booking while its release date is still
 * ahead of today. A missing/unparseable date counts as already released.
 */
export const isAdvanceBooking = (releaseDate?: Date | null): boolean =>
  !!releaseDate &&
  isValid(releaseDate) &&
  isAfter(startOfDay(releaseDate), startOfDay(new Date()));

/** Advance-booking movies first; the original order is kept within each group. */
export const sortAdvanceBookingFirst = <T>(
  movies: T[],
  getReleaseDate: (movie: T) => Date | null | undefined,
): T[] =>
  movies
    .slice()
    .sort(
      (a, b) =>
        Number(isAdvanceBooking(getReleaseDate(b))) -
        Number(isAdvanceBooking(getReleaseDate(a))),
    );

/**
 * Colour scheme for a show time button from the backend `show_status`
 * (`available` | `fast_filling` | `sold` | `expired`).
 */
export const getShowStatusColorScheme = (
  showStatus?: string,
): AvailabilityColorSchemeEnum => {
  switch (showStatus) {
    case "available":
      return AvailabilityColorSchemeEnum.AVAILABLE;
    case "fast_filling":
      return AvailabilityColorSchemeEnum.FAST_FILLING;
    case "sold":
      return AvailabilityColorSchemeEnum.SOLD_OUT;
    default:
      return AvailabilityColorSchemeEnum.NOT_AVAILABLE;
  }
};
